<template>
  <!-- En ámbar y no en rojo a propósito: el rojo se lee como «ha fallado», y aquí lo que se está
       diciendo es que no se sabe. -->
  <div class="aviso-dudoso">
    <p>{{ duda.texto }}</p>

    <!-- Lo que el servidor contestó de esa operación, cuando ya se le ha preguntado. Va aparte del
         texto porque es el dato y no la explicación. -->
    <p class="aviso-dato" v-if="detalle">{{ detalle }}</p>

    <div class="aviso-botones">
      <!-- «Volver a intentarlo» NO se ofrece con el resultado desconocido: ahí el servidor no la
           repite nunca —la operación quedó marcada como lanzada— así que el botón solo podría dar
           otra vez la misma respuesta. Ofrecerlo sería mandar a pulsar algo que no lleva a nada. -->
      <!-- Con otra operación de este mismo sitio en vuelo el botón se queda a la vista y apagado,
           no desaparece: es una espera de segundos y lo que hace falta es saber que vuelve. El
           motivo va debajo, junto a él (§9). -->
      <button class="aviso-btn aviso-btn-azul" v-if="sePuedeReintentar"
        :disabled="duda.consultando || !!motivoEspera" @click="$emit('reintentar')">
        Volver a intentarlo
      </button>
      <button class="aviso-btn" :disabled="duda.consultando" @click="$emit('comprobar')">
        {{ duda.consultando ? 'Comprobando…' : 'Comprobar cómo quedó' }}
      </button>
      <!-- Solo con el resultado desconocido, y es la única salida que hay ahí: el servidor no va a
           poder decir más. Se da por vista a mano, nunca sola. -->
      <button class="aviso-btn" v-if="esDesconocido" :disabled="duda.consultando"
        @click="$emit('dar-por-visto')">
        Lo doy por visto
      </button>
    </div>

    <!-- §9 · por qué no se puede usar y qué hacer, junto al control. -->
    <p class="aviso-nota" v-if="motivoEspera">{{ motivoEspera }}</p>
    <p class="aviso-nota" v-if="duda.noSePudo">{{ duda.noSePudo }}</p>
    <!-- Su clave no ha podido quedar guardada en el navegador, así que una recarga se la llevaría y
         con ella la única manera de preguntar cómo acabó. No se puede arreglar desde aquí —la
         petición ya salió—, pero sí se puede decir antes de que alguien recargue. -->
    <p class="aviso-nota" v-if="duda.sinApuntar">
      Y resuélvelo <strong>sin recargar la pantalla</strong>: el identificador de esta operación no
      se ha podido guardar en este navegador —el almacenamiento está lleno o bloqueado, y en modo
      privado no se puede—, así que si recargas se pierde y ya no se podrá preguntar cómo acabó.
      Resuelve las que tengas en «Operaciones sin resolver» para hacer sitio.
    </p>
    <p class="aviso-nota" v-if="esDesconocido">
      Esto no se va a poder saber con más certeza. Mira en «Envíos programados» y en «Últimos
      envíos» cómo ha quedado; si era un envío con «Enviar reporte», pregunta a quien lo iba a
      recibir. Después, «Lo doy por visto» desbloquea la pantalla.
    </p>
  </div>
</template>

<script>
/**
 * **El aviso de una operación cuyo resultado no se sabe**, en el informe de abonos abiertos
 * (COB-7, #193).
 *
 * POR QUÉ ES UN COMPONENTE Y NO TRES BLOQUES IGUALES. Porque esta pantalla tiene tres sitios donde
 * puede quedar una escritura en el aire —«Enviar reporte», el formulario de la programación y cada
 * regla de la lista— y el defecto que llevó tres rondas de revisión fue exactamente ese: cada
 * ronda señalaba uno de los tres y el parche se aplicaba solo ahí. Con el aviso en un sitio, los
 * tres dicen lo mismo y lo que se añada vale para los tres.
 *
 * LAS TRES SALIDAS, Y CUÁNDO SE OFRECE CADA UNA (decisión de @Donchurru del 15/10/2026, D + C):
 *
 *  - **Volver a intentarlo** — repite LA MISMA operación, con su misma clave, y el servidor no la
 *    aplica dos veces. No se ofrece con el resultado `DESCONOCIDO`: ahí el servidor no la repite
 *    nunca y el botón solo daría otra vez la misma respuesta.
 *  - **Comprobar cómo quedó** — le pregunta al servidor por esa operación. No es una relectura de
 *    ninguna lista: es el registro de operaciones, que es el que lo sabe. Esto es lo que sustituye
 *    al «Descartarlo» de antes, que daba por no hecho lo que no había comprobado.
 *  - **Lo doy por visto** — solo con `DESCONOCIDO`, que es la única situación que no se resuelve
 *    preguntando. La pulsa una persona a sabiendas; nunca se hace sola.
 */
export default {
  name: 'aviso-operacion-dudosa',
  props: {
    /**
     * `{ texto, situacion, consultando, noSePudo, detalle, sinApuntar }` tal como lo arma la pantalla
     * con lo que contesta el servidor. `situacion` es `POR_CONSULTAR` mientras no se ha preguntado, y
     * `sinApuntar` es «su clave no está guardada en el navegador», que se avisa aquí porque recargar
     * la perdería.
     */
    duda: { type: Object, required: true },
    /**
     * Por qué «Volver a intentarlo» no se puede pulsar **ahora mismo**, o `null`.
     *
     * Lo decide la pantalla, que es la que sabe qué más hay en vuelo en ese sitio: dos operaciones
     * del mismo ámbito se repiten de una en una, porque lanzadas a la vez la respuesta de una puede
     * deshacer lo de la otra. Aquí solo se pinta y se apaga el botón.
     */
    motivoEspera: { type: String, default: null }
  },
  computed: {
    esDesconocido: function() {
      return this.duda.situacion === 'DESCONOCIDO';
    },
    sePuedeReintentar: function() {
      return !this.esDesconocido;
    },
    /** El resultado que contestó el servidor, cuando hay algo que enseñar. */
    detalle: function() {
      return this.duda.detalle || null;
    }
  }
};
</script>

<style scoped>
.aviso-dudoso {
  margin-top: 10px;
  padding: 10px 12px;
  background: #FFF6E5;
  border: 1px solid #E8C37A;
  border-radius: 8px;
  color: #6B4A08;
  font-size: 12px;
  line-height: 1.4;
}
.aviso-dudoso p { margin: 0 0 8px; }
.aviso-dato { font-weight: 600; }
.aviso-nota { margin: 8px 0 0; font-size: 11px; color: #8E2B20; }

.aviso-botones { display: flex; flex-wrap: wrap; gap: 6px; }
.aviso-btn {
  /* Framework7 le da `width:100%` a todo <button> por selector de tipo: sin estas dos líneas cada
     botón ocupa una fila entera. */
  width: auto;
  display: inline-block;
  font-size: 11px;
  padding: 5px 12px;
  border: 1px solid #D8B873;
  border-radius: 4px;
  background: #ffffff;
  color: #6B4A08;
  cursor: pointer;
  font-weight: 500;
}
.aviso-btn-azul { background: #185FA5; color: #ffffff; border-color: #185FA5; }
.aviso-btn:focus-visible { outline: 2px solid #185FA5; outline-offset: 2px; }
/* Deshabilitado se tiene que VER deshabilitado: si no, la pulsación que no hace nada se lee como
   que la pantalla se ha colgado y se vuelve a pulsar. */
.aviso-btn:disabled { opacity: 0.5; cursor: default; }
</style>
